import React, { useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { Modal } from "../../components/Modal.jsx";
import { updateProject } from "../../data/projectsClient.js";
import { PROJECT_STAGES, isTerminalProject, projectDate, shouldNudgeCompletion } from "../../lib/projectStage.js";
import { ProjectStageControl, ProjectStagePill } from "./ProjectStageControl.jsx";

export function ProjectStageActions({ project, canEdit, onSaved, onEditorState }) {
  const key = `project-completion-nudge:${project.id}`;
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(key) === "dismissed");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const editorCb = useRef(onEditorState);
  editorCb.current = onEditorState;
  useEffect(() => () => editorCb.current?.({ dirty: false, busy: false, editing: false }), []);
  const today = projectDate(new Date());
  const nudge = canEdit && !dismissed && shouldNudgeCompletion(project, today);
  function close() {
    if (busy) return;
    setDraft(null); setError(null); onEditorState?.({ dirty: false, busy: false, editing: false });
  }
  async function save(patch) {
    if (busy) return;
    setBusy(true); setError(null); onEditorState?.({ dirty: true, busy: true, editing: true });
    try {
      const saved = await updateProject(project.id, patch);
      onSaved?.(saved); setDraft(null);
      onEditorState?.({ dirty: false, busy: false, editing: false });
    } catch (err) {
      setError(err.message);
      onEditorState?.({ dirty: Boolean(draft), busy: false, editing: Boolean(draft) });
    } finally { setBusy(false); }
  }
  return <>
    {nudge && <section data-testid="project-completion-nudge" style={{ padding: 12, marginBottom: 12, border: `1px solid ${T.border}`, background: T.utilAmber, borderRadius: 10 }}>
      <div>FDOB has passed and the project is in Close. Mark it completed?</div>
      <button type="button" onClick={() => { sessionStorage.setItem(key, "dismissed"); setDismissed(true); }}>Not yet</button>{" "}
      <button type="button" onClick={() => {
        setDraft({ stage: "completed", completedOn: projectDate(project.fdobDate) || today, stageNote: "" });
        onEditorState?.({ dirty: true, busy: false, editing: true });
      }}>Mark completed</button>
    </section>}
    {isTerminalProject(project) && <section aria-label="Stage" data-testid="project-stage-card"
      style={{ padding: 16, border: `1px solid ${T.border}`, borderRadius: 10, background: T.card, marginBottom: 12 }}>
      <ProjectStagePill stage={project.stage} />
      <p>{PROJECT_STAGES[project.stage]}{project.stage === "completed" ? ` on ${projectDate(project.completedOn) || "unknown date"}` : ""}
        {" by "}{project.stageAttribution?.actor || "unknown actor"}</p>
      {canEdit && <button type="button" disabled={busy} onClick={() => save({ stage: "active" })}>Reopen</button>}
      {error && !draft && <div role="alert">{error}</div>}
    </section>}
    {draft && <Modal isOpen title="Mark project completed" onClose={close} maxWidth={440} disableBackdropClose={busy}>
      <div style={{ padding: 20 }}>
        <p>{project.name} stays searchable and can be reopened.</p>
        <ProjectStageControl {...draft} note={draft.stageNote} disabled={busy}
          onChange={(field, value) => setDraft(current => ({ ...current, [field]: value }))} />
        {error && <div role="alert">{error}</div>}
        <button type="button" disabled={busy} onClick={close}>Cancel</button>{" "}
        <button type="button" disabled={busy} onClick={() => save({ ...draft, completedOn: draft.stage === "completed" ? draft.completedOn : null })}>
          {busy ? "Saving…" : "Save Stage"}
        </button>
      </div>
    </Modal>}
  </>;
}
